import React from "react"
import {
  formatCurrencyInThousands,
  formatPercent,
  formatRatio,
} from "./tableFormatters"
import {
  ToolTable,
  ToolTableHeader,
  ToolTableBody,
  ToolTableRow,
  ToolTableHead,
  ToolTableCell,
} from "@/ui/ToolTable"

const TopFiveSeriesTable = ({ data }: { data: any[] }) => {
  // Normalize data: convert string values to numbers if needed
  const normalizeRow = (row: any) => ({
    series: row.series,
    qpcsp_weighting: parseFloat(row.qpcsp_weighting) || 0,
    calls: parseFloat(row.calls) || 0,
    distributions: parseFloat(row.distributions) || 0,
    valuation: parseFloat(row.valuation) || 0,
    gross_moic:
      row.gross_moic !== null && row.gross_moic !== undefined
        ? parseFloat(row.gross_moic)
        : null,
    is_total: row.is_total || false,
  })

  return (
    <ToolTable>
      <ToolTableHeader>
        <ToolTableRow>
          <ToolTableHead>Series</ToolTableHead>
          <ToolTableHead>QPCSP Weighting</ToolTableHead>
          <ToolTableHead>Calls</ToolTableHead>
          <ToolTableHead>Distributions</ToolTableHead>
          <ToolTableHead>Valuation</ToolTableHead>
          <ToolTableHead>Gross MOIC</ToolTableHead>
        </ToolTableRow>
      </ToolTableHeader>
      <ToolTableBody>
        {data.map((row, index) => {
          const normalizedRow = normalizeRow(row)
          return (
            <ToolTableRow
              key={index}
              variant={normalizedRow.is_total ? "totals" : "default"}
            >
              <ToolTableCell>{normalizedRow.series}</ToolTableCell>
              <ToolTableCell>
                {formatPercent(normalizedRow.qpcsp_weighting)}
              </ToolTableCell>
              <ToolTableCell>
                {formatCurrencyInThousands(normalizedRow.calls)}
              </ToolTableCell>
              <ToolTableCell>
                {formatCurrencyInThousands(normalizedRow.distributions)}
              </ToolTableCell>
              <ToolTableCell>
                {formatCurrencyInThousands(normalizedRow.valuation)}
              </ToolTableCell>
              <ToolTableCell>
                {normalizedRow.gross_moic !== null
                  ? formatRatio(normalizedRow.gross_moic)
                  : "—"}
              </ToolTableCell>
            </ToolTableRow>
          )
        })}
      </ToolTableBody>
    </ToolTable>
  )
}

const EmptyTable = () => {
  return (
    <div className="editorjs-empty-tool-table">No table data available</div>
  )
}

const hasValidTableData = (data: any) => {
  return data && Array.isArray(data) && data.length > 0
}

export class TopFiveSeriesTableTool {
  api: any
  block: any
  createPortal: any
  removePortal: any
  portal: any
  tableData: any
  headerInserted: boolean

  constructor(toolConfig: any) {
    this.block = toolConfig.block
    this.api = toolConfig.api
    this.createPortal = toolConfig.config?.createPortal
    this.removePortal = toolConfig.config?.removePortal
    this.tableData = toolConfig.config?.tableData

    this.headerInserted = toolConfig.data?.header_inserted || false

    return this
  }

  static get toolbox() {
    return {
      title: "Top 5 Series",
      icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 3v18"/><path d="m16 15-2-2 2-2"/><path d="M21 9H3"/><path d="M21 15H3"/></svg>',
    }
  }

  render() {
    const rootEl = document.createElement("div")

    const hasData = hasValidTableData(this.tableData)

    // Check if we need to insert a header above this block
    queueMicrotask(() => {
      this.checkAndInsertHeaderIfNeeded()
    })

    this.portal = this.createPortal(
      this.block.id,
      hasData ? <TopFiveSeriesTable data={this.tableData} /> : <EmptyTable />,
      rootEl,
    )

    return rootEl
  }

  async checkAndInsertHeaderIfNeeded() {
    try {
      // Short-circuit if header has already been inserted
      if (this.headerInserted) {
        return
      }

      // Find this block's index by searching through all blocks
      let currentBlockIndex = -1
      const totalBlocks = this.api.blocks.getBlocksCount()

      for (let i = 0; i < totalBlocks; i++) {
        const block = this.api.blocks.getBlockByIndex(i)
        if (block && block.id === this.block.id) {
          currentBlockIndex = i
          break
        }
      }

      if (currentBlockIndex === -1) {
        console.error(
          "ERROR: Could not find current block, skipping header insertion",
        )
        return
      }

      if (currentBlockIndex > 0) {
        const previousBlock = this.api.blocks.getBlockByIndex(
          currentBlockIndex - 1,
        )

        if (previousBlock) {
          // Await the saved data from the previous block
          const previousBlockData = await previousBlock.save()

          // Check if it's a header block with our specific text and level
          if (
            previousBlockData.tool === "header" &&
            previousBlockData.data.text === "Top 5 Series" &&
            previousBlockData.data.level === 3
          ) {
            // Header exists - mark as inserted even though we didn't insert it
            this.headerInserted = true
            return
          }
        }
      }

      this.insertHeaderBlock(currentBlockIndex)
    } catch (error) {
      console.error("Error in checkAndInsertHeaderIfNeeded:", error)
    }
  }

  insertHeaderBlock(currentBlockIndex: number) {
    try {
      // Insert a header block above the current table block
      this.api.blocks.insert(
        "header",
        {
          text: "Top 5 Series",
          level: 3,
        },
        {},
        currentBlockIndex,
        true,
      )

      this.headerInserted = true

      // Move focus back to the table block after insertion
      setTimeout(() => {
        this.api.caret.setToBlock(currentBlockIndex + 1)
      }, 50)
    } catch (error) {
      console.error("Error inserting header block:", error)
    }
  }

  remove() {
    const totalBlocks = this.api.blocks.getBlocksCount()
    for (let i = 0; i < totalBlocks; i++) {
      const block = this.api.blocks.getBlockByIndex(i)
      if (block && block.id === this.block.id) {
        this.api.blocks.delete(i)
        break
      }
    }
  }

  removed() {
    this.removePortal(this.block.id)
  }

  save() {
    return {
      type: "top_five_series_table",
      header_inserted: this.headerInserted,
    }
  }

  static get isReadOnlySupported() {
    return true
  }
}
